import { useRef, useEffect, useCallback, useReducer, ChangeEvent } from 'react';
import qs from 'query-string';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import { EventData, Merge, Middleware } from '@carta/fep-analytics';
import useSelectable, {
  UseSelectableConfigType,
  UseSelectableStateType,
  UseSelectableActionsType,
} from './useSelectable';

export type Action = {
  type: string;
  payload?: Record<string, unknown> | string | number;
};

export type State = {
  response?: Record<string, unknown>;
  loading?: boolean;
  error?: boolean;
  message?: string | number | Record<string, unknown>;
  searchQuery?: string | number | Record<string, unknown>;
  orderingKey?: string | number | Record<string, unknown>;
  orderingDirection?: string | number | Record<string, unknown>;
  queryString?: string | number | Record<string, unknown>;
  filters?: string | number | Record<string, unknown>;
  selectedRows?: string | number | Record<string, unknown>;
  currentPageNumber?: string | number | Record<string, unknown>;
};

export type SortConfigType = {
  key: string;
  direction: string;
};

export type InitialQueryType = {
  ordering: string;
};

export type OrderingVarsType = {
  key?: string;
  direction: string;
};

export type OrderingParamType = {
  ordering: string;
};

export type SearchParamType = {
  search: string;
};

export type UseAnalyticsConfigType = {
  eventData: EventData;
  config: {
    dispatch?: Middleware;
    dispatchOnMount?: boolean;
    localDataOnly?: boolean;
    mergeData?: Merge;
  };
};

export type useLedgerConfigType = {
  url: string;
  getNumPages?: (data: any) => number;
  getCurrentPage?: (data: any) => number;
  getTotalItems?: (data: any) => number;
  pageSize?: number;
  sortConfig?: SortConfigType;
  selectableConfig?: UseSelectableConfigType;
  debounceTimeout?: number;
  dataKey?: string;
  defaultQueryParams?: Record<string, unknown>;
  stateToSearchParam?: (searchQuery: string) => SearchParamType;
  stateToOrderingParam?: (orderingDirection: string, orderingKey: string) => OrderingParamType;
  stateToFilterParams?: (filters: GetFilterPropsType) => Record<string, unknown>;
  makeRequest?: (
    url: string,
    queryString: string,
    signal?: AbortSignal,
  ) => Promise<{ results: Array<Record<string, unknown>> }>;
  useAnalyticsConfig?: UseAnalyticsConfigType | boolean;
  externals?: Record<string, unknown>;
};

export type GetFilterPropsType = {
  filters: Record<string, unknown>;
  getFilterValue: (filterName: string) => unknown;
  setFilterValue: (filterName: string, value: string) => void;
  replaceFilters: (filters: Record<string, unknown>) => void;
  clearFilters: () => void;
};

export type GetSearchPropsType = {
  value?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  clearSearch: () => void;
};

export type GetPaginationPropsType = {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onChange: (page: number) => void;
};

export type GetSortingPropsType = {
  sortByKey: (key: string, direction?: 'ascending' | 'descending') => { onClick: () => void };
  orderingKey?: string;
  orderingDirection: string;
};

export type GetBottomBarPropsType = {
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
  rows: Record<string, unknown>;
};

export type GetTopBarPropsType = {
  rows: Record<string, unknown>;
  selectableProps: UseSelectableActionsType;
};

export type UseLedgerType<T extends Record<string, any> = any> = State & {
  getFilterProps: () => GetFilterPropsType;
  getSearchProps: () => GetSearchPropsType;
  getPaginationProps: () => GetPaginationPropsType;
  getSortingProps: () => GetSortingPropsType;
  data: Array<T>;
  setData: (data: Array<T>) => void;
  selected: UseSelectableStateType;
  selectableProps: UseSelectableActionsType;
  getTopBarProps: () => GetTopBarPropsType;
  getBottomBarProps: () => GetBottomBarPropsType;
  refetch: () => void;
};

type StateType<T> = {
  response: Record<string, unknown>;
  queryString: string;
  searchQuery: string;
  orderingKey: string | undefined;
  orderingDirection: string;
  filters: Record<string, unknown>;
  currentPageNumber: string | number;
  data: Array<T>;
  externals: Record<string, unknown>;
  pageSize: number;
  loading?: boolean;
  error?: boolean;
  message?: string | number | Record<string, unknown>;
};

type ActionType<T> =
  | {
      type: 'loading-started';
    }
  | {
      type: 'loading-success';
      payload: Record<string, unknown>;
      data: Array<T>;
    }
  | {
      type: 'set-ordering-vars';
      direction: any;
      key: any;
    }
  | {
      type: 'loading-error';
      payload: string | number | Record<string, unknown>;
    }
  | {
      type: 'set-search-query' | 'set-query-string';
      payload: string;
    }
  | {
      type: 'set-current-page-number';
      payload: string | number;
    }
  | {
      type: 'set-filters';
      payload: Record<string, unknown>;
    }
  | {
      type: 'set-data';
      payload: Array<T>;
    };

type Reducer<T> = (state: StateType<T>, action: ActionType<T>) => StateType<T>;

/**
   The hook manages its state with this reducer. All variables related to search, sort, filter, and pagination
   are stored in this reducer's state.
*/
const reducer = <T>(state: StateType<T>, action: ActionType<T>): StateType<T> => {
  switch (action.type) {
    case 'loading-started':
      return { ...state, loading: true };
    case 'loading-success':
      return { ...state, loading: false, error: false, message: '', response: action.payload, data: action.data };
    case 'loading-error':
      return { ...state, loading: false, error: true, message: action.payload };
    case 'set-search-query':
      return { ...state, searchQuery: action.payload };
    case 'set-ordering-vars':
      return { ...state, orderingDirection: action.direction, orderingKey: action.key };
    case 'set-query-string':
      return { ...state, queryString: action.payload };
    case 'set-filters':
      return { ...state, filters: action.payload };
    case 'set-current-page-number':
      return { ...state, currentPageNumber: action.payload };
    case 'set-data':
      return { ...state, data: action.payload };
    default:
      return { ...state };
  }
};

/**
   Ordering functions
*/

/**
   The hook state keeps track of two ordering variables: orderingDirection, and orderingKey. These
   two variables are combined into a single string and passed into the query parameters for the API request.

   @param {string} orderingDirection "ascending", "descending", or an empty string.
   @param {string} orderingKey The unique key for the column by which you want the data to be sorted.
*/
const createOrderingQueryParam = (orderingDirection: string, orderingKey: string): OrderingParamType | undefined => {
  if (!orderingKey || !orderingDirection) {
    return { ordering: '' };
  }
  if (orderingDirection === 'descending') {
    return { ordering: `-${orderingKey}` };
  }
  return { ordering: orderingKey };
};

/**
   The hook state keeps track of two ordering variables: orderingDirection, and orderingKey. This function
   is able to extrapolate those variables based on the initial query parameters (which come from the URL and/or
   the default query options passed into the hook) and the sort config (which is passed into the hook).

   @param {initialQuery} initialQuery Object mapping out the variables in the initial query string paramaters.
   @param {sortConfig} sortConfig Sort config passed into the hook.
*/
const getOrderingVars = (initialQuery: Record<string, unknown>, sortConfig: SortConfigType): OrderingVarsType => {
  if (initialQuery.ordering === '') {
    return {
      key: sortConfig.key,
      direction: sortConfig.direction,
    };
  }
  const orderingRegex = new RegExp(/^(?<hyphen>-)?(?<orderingTerm>\w+)$/);
  const ordering = initialQuery.ordering as string;
  const orderingStringMatch = ordering.match(orderingRegex);
  if (!orderingStringMatch) {
    return {
      key: '',
      direction: '',
    };
  }

  return {
    key: orderingStringMatch?.groups?.orderingTerm,
    direction: orderingStringMatch.groups?.hyphen ? 'descending' : 'ascending',
  };
};

/**
   Functions to manage mapping the state to the URL in the browser
*/

/**
   Updates the URL in the browser based on the current query parameters

   @param {string} queryString query parameters concatenated to a string. Used to make queries to the API.
*/
const updateUrl = (queryString: string): void => {
  window.history.pushState('', '', `${window.location.href.replace(window.location.search, '')}?${queryString}`);
};

/**
   Parses a query string and returns an object with key/value pairs correlating to query parameters

   @param {string} queryString query parameters concatenated to a string. Used to make queries to the API.
*/
const getQueryStringDict = (queryString: string): Record<string, unknown> => {
  return qs.parse(queryString);
};

/**
   Filtering functions
*/

/**
   The hook is instantiated with a set of default query parameters. Any query parameters that are not in the
   default can be considered filters. This function parses the querystring dict to find the filter parameters,
   as defined as all query parameters that are not in the default query parameters.

   We define filters as all query parameters that are not in the default query parameters
   passed in by the user through the `defaultQueryParams` prop.

   @param {Object} queryDict An object of key/value pairs correlating to query parameters.
   @param {Object} defaultQueryParams The hook's default query parameters.
*/
const getFiltersFromQueryDict = (
  queryDict: Record<string, unknown>,
  defaultQueryParams: Record<string, unknown>,
): Record<string, unknown> => {
  const queryDictClone = { ...queryDict };
  const defaultFilters = Object.keys(defaultQueryParams);
  return Object.entries(queryDictClone).reduce((acc: any, current: any) => {
    const [param, value] = current;
    if (!defaultFilters.includes(param)) {
      acc[param] = value;
    }
    return acc;
  }, {});
};

/**
   Error handling
*/

/**
   If the user utilizes the `makeRequest` prop, it is vital that they define their function outside of the
   component body, or else it will infinitely update. This function handles that error state.

   @param {function} func The `makeRequest` function the user has passed into the config.
*/
const useDetectUnstableFunction = func => {
  const ref = useRef(func);

  useEffect(() => {
    if (ref.current && func !== ref.current) {
      throw new Error(
        `"makeRequest" changed in between renders. This will cause infinite updating loops. \
Try defining "makeRequest" outside of a component body to give it a stable identity.`,
      );
    }

    ref.current = func;
  });
};

/**
   Default config objects. It is important to define these outside the scope of the hook, because Javascript
   equality checks will force the hook to rerender indefinitely if they are defined in the hook body.
*/
const defaultQueryOptions = { page: '1', page_size: '50', search: '', ordering: '' };
const defaultGetNumPages = results => results.num_pages;
const defaultGetCurrentPage = results => results.page;
const defaultGetTotalItems = results => results.count;
const defaultMakeRequest = (url, queryString, signal) => fetch(`${url}?${queryString}`, { signal }).then(r => r.json());
const defaultSelectableConfig = { key: 'id', initialState: [] };
const defaultSortConfig = { key: '', direction: '' };
const defaultStateToFilterParam = (filters: GetFilterPropsType) => {
  return filters;
};
const defaultStateToSearchParam = (searchQuery: string) => {
  return { search: searchQuery };
};

const useLedger = <T extends Record<string, any> = any>(config: useLedgerConfigType): UseLedgerType<T> => {
  const {
    url,
    getNumPages = defaultGetNumPages,
    getCurrentPage = defaultGetCurrentPage,
    getTotalItems = defaultGetTotalItems,
    pageSize = 50,
    sortConfig = defaultSortConfig,
    selectableConfig = defaultSelectableConfig,
    debounceTimeout = 250,
    dataKey = 'results',
    defaultQueryParams = defaultQueryOptions,
    stateToSearchParam = defaultStateToSearchParam,
    stateToOrderingParam = createOrderingQueryParam,
    stateToFilterParams = defaultStateToFilterParam,
    makeRequest = defaultMakeRequest,
    externals = {},
  } = config;

  useDetectUnstableFunction(makeRequest);

  /**
    Ledger URLs have query string variables in them. This way, users can share links
    with sorting/searching/filtering/pagination and expect the ledger to reflect as those things.

    Because of this, we need to parse the initial URL for query variables and set the
    initial state accordingly.

    The logic goes as follows:
    - If the user passes in custom defaultQueryParams, we just use those.
    - If the user does not, we take query params out of the url.
  */
  const queryParamsFromUrl = getQueryStringDict(window.location.search.substring(1));
  const getInitialQuery = (): Record<string, unknown> => {
    if (!isEqual(defaultQueryParams, defaultQueryOptions)) {
      return defaultQueryParams;
    }
    return { ...defaultQueryOptions, ...queryParamsFromUrl };
  };

  const initialQuery = getInitialQuery();
  const initialFilters = getFiltersFromQueryDict(initialQuery, defaultQueryParams);
  const orderingVars = getOrderingVars(initialQuery, sortConfig);

  /**
    Set the internal state with a reducer.
  */
  const [state, dispatch] = useReducer<Reducer<T>>(reducer, {
    response: {},
    queryString: window.location.search.substring(1),
    searchQuery: initialQuery.search,
    orderingKey: orderingVars.key,
    orderingDirection: orderingVars.direction,
    filters: initialFilters,
    currentPageNumber: initialQuery.page,
    data: [],
    externals,
    pageSize,
  } as StateType<T>);

  const [selected, selectableActions] = useSelectable(state.data, selectableConfig);

  /**
    Assemble the API call query string.
  */
  const { orderingDirection, orderingKey, filters, searchQuery } = state;

  const queryOptions = {
    ...defaultQueryOptions,
    ...stateToSearchParam(searchQuery),
    ...stateToOrderingParam(orderingDirection, orderingKey || ''),
    page: state.currentPageNumber,
    page_size: pageSize,
    ...stateToFilterParams(filters as GetFilterPropsType),
    ...externals,
  };

  const qstring = qs.stringify(queryOptions);
  useEffect(() => {
    dispatch({ type: 'set-query-string', payload: qstring });
  }, [qstring]);

  /**
   The following two functions are responsible for making the API request that
   returns the data that populates the ledger.

   It calls the makeRequest function (either the ledger default or passed into the
   config by the user) after a debounce period, and then dispatches an action to the
   reducer.
  */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const makeDebouncedRequest = useCallback(
    debounce(
      (url: string, queryString: string, signal?: AbortSignal) =>
        makeRequest(url, queryString, signal)
          .then(json => {
            const response = json as Record<string, unknown>;
            if (dataKey in response && Array.isArray(response[dataKey])) {
              dispatch({ type: 'loading-success', payload: response, data: response[dataKey] as T[] });
              updateUrl(queryString);
            } else {
              dispatch({
                type: 'loading-error',
                payload: `The provided dataKey: ${dataKey} is not a property of the request response`,
              });
            }
          })
          .catch(error => {
            if (error.name === 'AbortError') {
              /*
                If the error is an AbortError, it's expected and shouldn't cause the
                ledger to go into an error state (state.error = true) or change the
                loading state (state.loading = false). So we don't do anything.
              */
              return;
            }
            dispatch({ type: 'loading-error', payload: error.message });
          }),
      debounceTimeout,
    ),
    [debounceTimeout],
  );

  useEffect(() => {
    dispatch({ type: 'loading-started' });

    const abortController = new AbortController();

    makeDebouncedRequest(url, state.queryString, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [url, state.queryString, makeDebouncedRequest]);

  /**
    This useEffect handles the case in which a user changes any of the query
    arguments (for example, going to the next page, or adding a search term), and
    then presses the back button.

    In that case, the window dispatches a popstate event, and the URL changes to
    reflect the previous state. At that time, we need to reset the query string
    so the query results match the url.

    In addition to matching the query string, we need to restore all of the internal ledger
    state variables - search, page number, and filters.
  */
  useEffect(() => {
    const updateStateOnUrlChange = () => {
      dispatch({ type: 'set-query-string', payload: window.location.search.substring(1) });
      const qStringDict = getQueryStringDict(window.location.search.substring(1));
      if (qStringDict.search !== state.searchQuery) {
        dispatch({ type: 'set-search-query', payload: qStringDict.search as string });
      }
      if (qStringDict.page !== state.currentPageNumber) {
        dispatch({ type: 'set-current-page-number', payload: qStringDict.page as number | string });
      }
      const filters = getFiltersFromQueryDict(qStringDict, defaultQueryParams);
      if (!isEqual(filters, state.filters)) {
        dispatch({ type: 'set-filters', payload: filters });
      }
    };

    window.addEventListener('popstate', updateStateOnUrlChange);
    return () => window.removeEventListener('popstate', updateStateOnUrlChange, true);
  }, [defaultQueryParams, state.currentPageNumber, state.filters, state.searchQuery]);

  /**
    Search functions
  */
  const clearSearch = (): void => {
    dispatch({ type: 'set-search-query', payload: '' });
  };

  /**
    Ordering functions
  */
  const changeConfigDirection = (key: string): string =>
    state.orderingKey === key && state.orderingDirection === 'ascending' ? 'descending' : 'ascending';

  const sortByKey = (key: string, direction?: 'ascending' | 'descending') => {
    const newDirection = direction || changeConfigDirection(key);
    return {
      onClick: () => {
        dispatch({ type: 'set-ordering-vars', direction: newDirection, key });
      },
      direction: key === state.orderingKey ? state.orderingDirection : undefined,
    };
  };

  /**
    Filtering functions
  */
  const getFilterValue = (filterName: string) => {
    if (!(filterName in state.filters)) {
      undefined;
    }
    return state.filters[filterName];
  };

  const setFilterValue = (filterName: string, value: string): void => {
    const newFilterValues = { ...state.filters };
    if (!newFilterValues[filterName] || newFilterValues[filterName] !== value) {
      newFilterValues[filterName] = value;
    } else if (newFilterValues[filterName] === value) {
      delete newFilterValues[filterName];
    }
    dispatch({ type: 'set-filters', payload: newFilterValues });
  };

  const replaceFilters = (filters: Record<string, unknown>): void => {
    dispatch({ type: 'set-filters', payload: filters });
  };

  const clearFilters = (): void => {
    dispatch({ type: 'set-filters', payload: {} });
  };

  /**
    Organize and return the data to the user.
  */
  const getSearchProps = (): GetSearchPropsType => {
    return {
      value: state.searchQuery,
      onChange: e => dispatch({ type: 'set-search-query', payload: e.target.value }),
      clearSearch,
    };
  };

  const getPaginationProps = (): GetPaginationPropsType => {
    return {
      currentPage: getCurrentPage(state.response),
      totalPages: getNumPages(state.response),
      totalItems: getTotalItems(state.response),
      onChange: page => dispatch({ type: 'set-current-page-number', payload: page }),
    };
  };

  const getSortingProps = (): GetSortingPropsType => {
    return {
      sortByKey,
      orderingKey: state.orderingKey,
      orderingDirection: state.orderingDirection,
    };
  };

  const getFilterProps = (): GetFilterPropsType => ({
    filters: state.filters,
    getFilterValue,
    setFilterValue,
    replaceFilters,
    clearFilters,
  });

  const getTopBarProps = () => ({
    ...getSearchProps(),
    ...getFilterProps(),
    selectableProps: selectableActions,
    rows: selected.rows,
  });

  const getBottomBarProps = () => ({
    ...getPaginationProps(),
    rows: selected.rows,
  });

  const setData = (data: Array<T>) => dispatch({ type: 'set-data', payload: data });

  return {
    ...state,
    setData,
    getFilterProps,
    getSearchProps,
    getPaginationProps,
    getSortingProps,
    selected,
    selectableProps: selectableActions,
    getTopBarProps,
    getBottomBarProps,
    refetch: () => {
      dispatch({ type: 'loading-started' });
      makeDebouncedRequest(url, state.queryString);
    },
  };
};

export default useLedger;
